import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Assessment,
  Receipt,
  AccountBalance,
  TrendingUp,
  Download,
  Visibility,
  PictureAsPdf,
  TableChart,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { reportService } from '../../services/reportService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';
import ReportViewer from './ReportViewer';

interface ReportType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  category: string;
}

const reportTypes: ReportType[] = [
  {
    id: 'profit-loss',
    name: 'Profit & Loss Report',
    description: 'Comprehensive profit and loss statement with revenue and expense breakdown',
    icon: <TrendingUp />,
    color: '#10B981',
    category: 'Financial',
  },
  {
    id: 'gst',
    name: 'GST Report',
    description: 'GST compliance report for tax filing with detailed transaction records',
    icon: <Receipt />,
    color: '#F97316',
    category: 'Tax',
  },
  {
    id: 'cash-flow',
    name: 'Cash Flow Report',
    description: 'Cash flow analysis showing operating, investing, and financing activities',
    icon: <AccountBalance />,
    color: '#06B6D4',
    category: 'Financial',
  },
  {
    id: 'staff-performance',
    name: 'Staff Performance Report',
    description: 'Detailed staff performance metrics including revenue and utilization',
    icon: <Assessment />,
    color: '#8B5CF6',
    category: 'Operations',
  },
];

const ReportGenerator: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleGenerateReport = async () => {
    if (!selectedReport) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select a report type',
      }));
      return;
    }

    try {
      setLoading(true);
      const startDateStr = startDate.toISOString();
      const endDateStr = endDate.toISOString();

      let data;
      switch (selectedReport) {
        case 'profit-loss':
          data = await reportService.generateProfitLossReport(startDateStr, endDateStr);
          break;
        case 'gst':
          data = await reportService.generateGSTReport(startDateStr, endDateStr);
          break;
        case 'cash-flow':
          data = await reportService.generateCashFlowReport(startDateStr, endDateStr);
          break;
        case 'staff-performance':
          data = await reportService.generateStaffPerformanceReport(startDateStr, endDateStr);
          break;
        default:
          throw new Error('Invalid report type');
      }

      setReportData(data);
      setViewerOpen(true);

      dispatch(addNotification({
        type: 'success',
        message: 'Report generated successfully',
      }));
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to generate report',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedReport) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select a report type',
      }));
      return;
    }

    try {
      setLoading(true);
      await reportService.downloadReport({
        reportType: selectedReport as any,
        format: exportFormat,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      dispatch(addNotification({
        type: 'success',
        message: 'Report exported successfully',
      }));
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to export report',
      }));
    } finally {
      setLoading(false);
    }
  };

  const selectedReportType = reportTypes.find(r => r.id === selectedReport);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
            Report Generator
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate comprehensive business reports for analysis and compliance
          </Typography>
        </Box>

        {/* Report Selection */}
        <Grid container spacing={3} mb={4}>
          {reportTypes.map((report) => (
            <Grid item xs={12} md={6} key={report.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedReport === report.id ? `2px solid ${report.color}` : '1px solid #e0e0e0',
                  borderRadius: 3,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                    borderColor: report.color,
                  },
                }}
                onClick={() => setSelectedReport(report.id)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar
                      sx={{
                        bgcolor: report.color,
                        width: 56,
                        height: 56,
                        color: 'white',
                      }}
                    >
                      {report.icon}
                    </Avatar>
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="h6" fontWeight={700}>
                          {report.name}
                        </Typography>
                        <Chip
                          label={report.category}
                          size="small"
                          sx={{
                            bgcolor: `${report.color}20`,
                            color: report.color,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {report.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Report Configuration */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Report Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date || dayjs())}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date || dayjs())}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as 'csv' | 'pdf')}
                    label="Export Format"
                  >
                    <MenuItem value="csv">
                      <Box display="flex" alignItems="center" gap={1}>
                        <TableChart fontSize="small" />
                        CSV
                      </Box>
                    </MenuItem>
                    <MenuItem value="pdf">
                      <Box display="flex" alignItems="center" gap={1}>
                        <PictureAsPdf fontSize="small" />
                        PDF
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box display="flex" gap={1} height="100%">
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Visibility />}
                    onClick={handleGenerateReport}
                    disabled={loading || !selectedReport}
                    fullWidth
                    sx={{
                      bgcolor: selectedReportType?.color || '#E8B4B8',
                      '&:hover': {
                        bgcolor: selectedReportType?.color || '#D4969B',
                      },
                    }}
                  >
                    {loading ? 'Generating...' : 'Generate'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportReport}
                    disabled={loading || !selectedReport}
                    sx={{
                      borderColor: selectedReportType?.color || '#E8B4B8',
                      color: selectedReportType?.color || '#E8B4B8',
                      '&:hover': {
                        borderColor: selectedReportType?.color || '#D4969B',
                        bgcolor: `${selectedReportType?.color || '#E8B4B8'}10`,
                      },
                    }}
                  >
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Selected Report Preview */}
        {selectedReportType && (
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${selectedReportType.color}20`,
              bgcolor: `${selectedReportType.color}05`,
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                sx={{
                  bgcolor: selectedReportType.color,
                  width: 40,
                  height: 40,
                  color: 'white',
                }}
              >
                {selectedReportType.icon}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedReportType.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Period: {startDate.format('MMM DD, YYYY')} - {endDate.format('MMM DD, YYYY')}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedReportType.description}
            </Typography>
          </Paper>
        )}

        {/* Report Viewer Dialog */}
        <ReportViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          reportType={selectedReport}
          reportData={reportData}
          startDate={startDate.format('YYYY-MM-DD')}
          endDate={endDate.format('YYYY-MM-DD')}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ReportGenerator;